import * as React from 'react';

/** `scale` is the pinch-zoom factor (1 when not zoomed). */
export type ViewportBox = { top: number; height: number; scale: number };

/**
 * The part of the page the person can see right now, in layout-viewport pixels: it shrinks when a
 * phone's on-screen keyboard opens and moves when the browser scrolls to keep a field in view.
 * A `position: fixed` element given this `top` and `height` sits exactly in the visible area.
 * Null while disabled, or where the browser has no `visualViewport`.
 */
export function useVisualViewportBox(enabled: boolean): ViewportBox | null {
  const [box, setBox] = React.useState<ViewportBox | null>(null);
  React.useEffect(() => {
    const viewport = typeof window === 'undefined' ? undefined : window.visualViewport;
    if (!enabled || !viewport) {
      setBox(null);
      return undefined;
    }
    let frame = 0;
    const measure = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const next = { top: Math.round(viewport.offsetTop), height: Math.round(viewport.height), scale: viewport.scale || 1 };
        setBox(current => (current && current.top === next.top && current.height === next.height && current.scale === next.scale ? current : next));
      });
    };
    measure();
    viewport.addEventListener('resize', measure);
    viewport.addEventListener('scroll', measure);
    return () => {
      window.cancelAnimationFrame(frame);
      viewport.removeEventListener('resize', measure);
      viewport.removeEventListener('scroll', measure);
    };
  }, [enabled]);
  return box;
}
